import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, MessageSquare, Calendar, Edit, MapPin, Zap,
  ListTodo, IndianRupee, ChevronDown, ChevronUp, AlertTriangle, Loader2,
  Copy, Check, Clock, User, Building2, Globe, Tag, BarChart3, Plus,
  Send, Trash2, CheckCircle, XCircle, MoreHorizontal, FileText
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO, isToday, isYesterday } from "date-fns";

import {
  LeadStageProgress,
  StageChangeDialog,
  EditLeadDialog,
  AddTaskDialog,
  AddMeetingDialog,
  LEAD_STAGES
} from "@/components/leads";
import { useCustomFieldValues } from "@/components/leads/CustomFieldsRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="text-slate-400 hover:text-slate-600 ml-1">
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

export default function LeadDetails() {
  const { leadId } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("capture");

  // Remarks
  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [loadingRemarks, setLoadingRemarks] = useState(false);

  // Follow-ups
  const [followups, setFollowups] = useState({ current: null, history: [], total: 0 });
  const [loadingFollowups, setLoadingFollowups] = useState(false);

  // Activities
  const [activities, setActivities] = useState([]);

  // Custom fields
  const { customValues, setCustomValue, saveCustomValues } = useCustomFieldValues(leadId);
  const [customFields, setCustomFields] = useState([]);

  // Dialogs
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);
  const [stageChangeNote, setStageChangeNote] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", due_date: "", priority: "medium" });
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ title: "", date: "", time: "", duration: "30" });

  // Follow-up scheduling
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [followUpData, setFollowUpData] = useState({ date: "", type: "call", notes: "" });

  // Insights
  const [insights, setInsights] = useState({
    totalDeals: 0, expectedRevenue: 0, probability: 0,
    lastContacted: null, nextFollowUp: null,
    totalInteractions: 0, engagementScore: 0
  });

  const calculateInsights = (d) => {
    const score = Math.min(100, Math.floor(
      (d.interaction_count || 0) * 10 + (d.deal_value ? 20 : 0) +
      (d.email ? 10 : 0) + (d.phone ? 10 : 0) +
      (d.stage === "qualified" ? 20 : d.stage === "proposal" ? 30 : 0)
    ));
    setInsights({
      totalDeals: d.deals_count || 0, expectedRevenue: d.deal_value || 0,
      probability: d.stage === "won" ? 100 : d.stage === "lost" ? 0 :
        d.stage === "qualified" ? 60 : d.stage === "proposal" ? 75 : 30,
      lastContacted: d.last_activity_date, nextFollowUp: d.follow_up_date || d.next_meeting,
      totalInteractions: d.interaction_count || 0, engagementScore: score
    });
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return "Today";
      if (isYesterday(date)) return "Yesterday";
      return format(date, "MMM d, yyyy");
    } catch { return dateStr; }
  };

  // ---- Fetchers ----
  const fetchLead = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, { credentials: "include" });
      if (!res.ok) { if (res.status === 404) { toast.error("Lead not found"); navigate("/leads"); return; } throw new Error(); }
      const data = await res.json();
      setLead(data); setEditForm(data); calculateInsights(data);
    } catch (e) { console.error(e); toast.error("Failed to load lead"); }
    finally { setLoading(false); }
  }, [leadId, navigate]);

  const fetchRemarks = useCallback(async () => {
    setLoadingRemarks(true);
    try { const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/remarks`, { credentials: "include" }); if (res.ok) setRemarks(await res.json()); }
    catch (e) { console.error(e); } finally { setLoadingRemarks(false); }
  }, [leadId]);

  const fetchFollowups = useCallback(async () => {
    setLoadingFollowups(true);
    try { const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/followups`, { credentials: "include" }); if (res.ok) setFollowups(await res.json()); }
    catch (e) { console.error(e); } finally { setLoadingFollowups(false); }
  }, [leadId]);

  const fetchActivities = useCallback(async () => {
    try { const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/activities`, { credentials: "include" }); if (res.ok) setActivities(await res.json()); }
    catch (e) { console.error(e); }
  }, [leadId]);

  const fetchCustomFields = useCallback(async () => {
    try { const res = await fetch(`${BACKEND_URL}/api/lead-fields`, { credentials: "include" }); if (res.ok) setCustomFields((await res.json()).filter(f => !f.is_system && f.is_active)); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (leadId) { fetchLead(); fetchRemarks(); fetchFollowups(); fetchActivities(); fetchCustomFields(); }
  }, [leadId, fetchLead, fetchRemarks, fetchFollowups, fetchActivities, fetchCustomFields]);

  // ---- Handlers ----
  const handleAddRemark = async () => {
    if (!newRemark.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/remarks`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ text: newRemark }),
      });
      if (res.ok) { toast.success("Remark added"); setNewRemark(""); fetchRemarks(); }
    } catch { toast.error("Error adding remark"); }
  };

  const handleDeleteRemark = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/remarks/${id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Deleted"); fetchRemarks(); }
    } catch { toast.error("Error"); }
  };

  const handleScheduleFollowUp = async () => {
    if (!followUpData.date) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ next_meeting: followUpData.date, meeting_notes: followUpData.notes, meeting_completed: false, follow_up_type: followUpData.type })
      });
      if (res.ok) { toast.success("Follow-up scheduled"); setShowFollowUpForm(false); setFollowUpData({ date: "", type: "call", notes: "" }); fetchLead(); fetchFollowups(); }
    } catch { toast.error("Error"); }
  };

  const handleCompleteFollowUp = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/meeting-action`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ action: "complete" })
      });
      if (res.ok) { toast.success("Completed"); fetchLead(); fetchFollowups(); }
    } catch { toast.error("Error"); }
  };

  const handleStageClick = (stage) => { if (stage.key !== lead?.stage) { setPendingStage(stage); setShowStageDialog(true); } };

  const confirmStageChange = async () => {
    if (!pendingStage) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ stage: pendingStage.label || pendingStage.key, stage_change_note: stageChangeNote })
      });
      if (res.ok) { toast.success(`Moved to ${pendingStage.label}`); setShowStageDialog(false); setStageChangeNote(""); fetchLead(); }
    } catch { toast.error("Failed"); } finally { setSaving(false); setPendingStage(null); }
  };

  const handleUpdateLead = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(editForm)
      });
      if (res.ok) { await saveCustomValues(); toast.success("Updated"); setShowEditDialog(false); fetchLead(); }
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };

  const handleSaveTask = async () => {
    if (!newTask.title.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(newTask)
      });
      if (res.ok) { toast.success("Task created"); setShowTaskDialog(false); setNewTask({ title: "", due_date: "", priority: "medium" }); }
    } catch { toast.error("Failed"); }
  };

  const handleCall = () => { if (lead?.phone) window.open(`tel:${lead.phone}`, "_self"); };
  const handleWhatsApp = () => {
    if (!lead?.phone) return;
    const digits = String(lead.phone).replace(/\D/g, "");
    // wa.me requires the number in international format with no plus / spaces;
    // pre-fill a friendly opener so the rep can edit before sending
    const text = encodeURIComponent(`Hi ${lead.name || ""}, this is regarding your enquiry.`);
    window.open(`https://wa.me/${digits}?text=${text}`, "_blank");
  };
  const handleJumpToAddRemark = () => {
    setActiveTab("remarks");
    // Wait one tick for the tab to mount the input, then focus + scroll into view
    setTimeout(() => {
      const el = document.querySelector('[data-testid="lead-remark-input"]');
      if (el) {
        try { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
      }
    }, 80);
  };
  const handleEmail = () => { if (lead?.email) window.open(`mailto:${lead.email}`, "_self"); };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!lead) return <div className="flex flex-col items-center justify-center h-96 text-slate-500"><AlertTriangle className="w-12 h-12 mb-4" /><p>Lead not found</p><Button className="mt-4" onClick={() => navigate("/leads")}>Back to Leads</Button></div>;

  const isEmpty = (v) => !v || v === "N/A" || v === "-";

  // stageColor not used as dynamic tailwind class

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/50">
      {/* Top Bar */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 px-4 lg:px-6 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/leads")} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors group">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-sm font-medium">Leads</span>
            </button>
            <span className="text-slate-200">/</span>
            <span className="text-sm font-semibold text-slate-900">{lead.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs" onClick={() => setShowEditDialog(true)}>
              <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* ═══════ LEFT SIDEBAR ═══════ */}
        <div className="w-full lg:w-[340px] xl:w-[360px] lg:min-h-screen bg-white border-b lg:border-b-0 lg:border-r border-slate-200/60 shrink-0">
          {/* Profile Card with gradient header */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-6 pb-16 relative">
            {/* gradient overlay */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "20px 20px" }} />
          </div>
          <div className="px-5 lg:px-6 -mt-12 relative z-10">
            <div className="flex flex-col items-center text-center mb-5">
              <Avatar className="w-24 h-24 border-4 border-white shadow-xl mb-3 ring-4 ring-white/50">
                <AvatarImage src={lead.avatar_url} />
                <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-2xl font-bold">
                  {getInitials(lead.name)}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold text-slate-900 mt-1">{lead.name}</h2>
              {lead.company && <p className="text-sm text-slate-500 mt-0.5">{lead.company}</p>}
              <Badge className={`mt-2 text-xs font-semibold px-3 py-1 ${lead.stage === "won" || lead.stage === "completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : lead.stage === "lost" ? "bg-red-100 text-red-700 border-red-200" : "bg-indigo-100 text-indigo-700 border-indigo-200"}`}>
                {lead.stage || "New Lead"}
              </Badge>
            </div>

            {/* Quick Actions — labeled, full-width on mobile */}
            <div className="grid grid-cols-1 gap-2 mb-5">
              <Button
                onClick={handleCall}
                disabled={!lead.phone}
                className="w-full h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm disabled:bg-slate-200 disabled:text-slate-400"
                data-testid="lead-action-call"
              >
                <Phone className="w-4 h-4 mr-2" /> Call now
              </Button>
              <Button
                onClick={handleWhatsApp}
                disabled={!lead.phone}
                className="w-full h-10 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium shadow-sm disabled:bg-slate-200 disabled:text-slate-400"
                data-testid="lead-action-whatsapp"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2 fill-current" aria-hidden="true">
                  <path d="M20.52 3.48A11.78 11.78 0 0012.06 0C5.5 0 .17 5.33.17 11.89c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.67a11.85 11.85 0 005.72 1.46h.01c6.56 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.17-3.44-8.42zM12.07 21.5h-.01a9.6 9.6 0 01-4.9-1.34l-.35-.21-3.76.99 1-3.67-.23-.38a9.59 9.59 0 01-1.47-5.1c0-5.31 4.32-9.62 9.63-9.62 2.57 0 4.98 1 6.8 2.82a9.55 9.55 0 012.81 6.81c0 5.31-4.32 9.63-9.63 9.63zm5.27-7.21c-.29-.14-1.71-.84-1.97-.93-.26-.1-.45-.14-.64.14-.19.29-.74.93-.91 1.12-.17.19-.34.21-.62.07-.29-.14-1.22-.45-2.32-1.44-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.45.13-.59.13-.13.29-.34.43-.5.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.64-1.55-.88-2.12-.23-.55-.47-.48-.64-.49l-.55-.01a1.06 1.06 0 00-.77.36c-.26.29-1 .98-1 2.39 0 1.41 1.03 2.78 1.17 2.97.14.19 2.03 3.1 4.92 4.34.69.3 1.22.48 1.64.61.69.22 1.31.19 1.81.12.55-.08 1.71-.7 1.95-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.33z"/>
                </svg>
                Connect with WhatsApp
              </Button>
              <Button
                onClick={handleJumpToAddRemark}
                variant="outline"
                className="w-full h-10 rounded-full border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300 font-medium"
                data-testid="lead-action-add-remark"
              >
                <MessageSquare className="w-4 h-4 mr-2" /> Add remark
              </Button>
            </div>

            {/* Contact Info */}
            <div className="space-y-1 mb-5">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Contact Info</h3>
              {lead.email && (
                <div className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-indigo-50/50 group transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-indigo-600" />
                  </div>
                  <span className="text-sm text-slate-700 truncate flex-1">{lead.email}</span>
                  <CopyButton text={lead.email} />
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-green-50/50 group transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-sm text-slate-700 flex-1">{lead.phone}</span>
                  <CopyButton text={lead.phone} />
                </div>
              )}
              {lead.city && (
                <div className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-amber-50/50 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-amber-600" />
                  </div>
                  <span className="text-sm text-slate-700">{lead.city}</span>
                </div>
              )}
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-4" />

            {/* Properties */}
            <div className="space-y-4">
              {[
                { label: "Date Created", value: formatDateDisplay(lead.created_at), show: true },
                { label: "Lead Source", value: lead.source, icon: Globe, show: !!lead.source },
                { label: "Deal Value", value: lead.deal_value ? `₹${Number(lead.deal_value).toLocaleString("en-IN")}` : null, show: !!lead.deal_value, bold: true },
              ].filter(p => p.show).map(p => (
                <div key={p.label} className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-medium">{p.label}</span>
                  <span className={`text-sm ${p.bold ? "font-bold text-emerald-600" : "font-medium text-slate-900"}`}>{p.value}</span>
                </div>
              ))}

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">Assigned to</span>
                <div className="flex items-center gap-2">
                  <Avatar className="w-5 h-5">
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 text-[9px] font-bold">{getInitials(lead.assigned_to_name || "U")}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-slate-900">{lead.assigned_to_name || "Unassigned"}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">Status</span>
                <Badge variant="outline" className="text-xs">{lead.lead_status || lead.stage || "New"}</Badge>
              </div>

              {/* Custom Fields */}
              {customFields.map(f => {
                const val = customValues[f.field_key] || customValues[f.field_id];
                if (!val || isEmpty(val)) return null;
                return (
                  <div key={f.field_key} className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 font-medium">{f.field_label}</span>
                    <span className="text-sm font-medium text-slate-900 max-w-[180px] truncate">{val}</span>
                  </div>
                );
              })}
            </div>

            <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-4" />

            {/* Lead Insights */}
            <div className="space-y-3 pb-6">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Lead Insights</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl p-3 border border-indigo-100">
                  <p className="text-[10px] text-indigo-500 font-semibold uppercase">Deal Value</p>
                  <p className="text-lg font-bold text-indigo-700">₹{Number(insights.expectedRevenue || 0).toLocaleString("en-IN")}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-3 border border-blue-100">
                  <p className="text-[10px] text-blue-500 font-semibold uppercase">Probability</p>
                  <p className="text-lg font-bold text-blue-700">{insights.probability}%</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-3 border border-emerald-100">
                  <p className="text-[10px] text-emerald-500 font-semibold uppercase">Interactions</p>
                  <p className="text-lg font-bold text-emerald-700">{insights.totalInteractions}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-3 border border-amber-100">
                  <p className="text-[10px] text-amber-500 font-semibold uppercase">Score</p>
                  <p className="text-lg font-bold text-amber-700">{insights.engagementScore}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm bg-slate-50 rounded-xl p-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Last Contacted</span>
                  <span className="font-medium text-slate-700">{formatDateDisplay(insights.lastContacted)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Next Follow-up</span>
                  <span className="font-medium text-slate-700">{formatDateDisplay(insights.nextFollowUp)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* ═══════ RIGHT CONTENT AREA ═══════ */}
          <div className="flex-1 min-w-0">
            {/* Stage Progress */}
            <LeadStageProgress lead={lead} onStageClick={handleStageClick} />

            {/* Tabs */}
            <div className="px-4 lg:px-6 pb-8">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-white border border-slate-200/60 rounded-xl w-full justify-start h-auto p-1 gap-1 shadow-sm overflow-x-auto flex-nowrap">
                  {[
                    { value: "capture", label: "Capture Details", icon: FileText },
                    { value: "activity", label: "Activity", icon: Clock },
                    { value: "remarks", label: "Remarks", count: remarks.length, icon: MessageSquare },
                    { value: "followups", label: "Follow-ups", count: followups.total || 0, icon: Calendar },
                  ].map(tab => (
                    <TabsTrigger key={tab.value} value={tab.value}
                      className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-all shrink-0 whitespace-nowrap">
                      <tab.icon className="w-4 h-4 mr-1.5" />
                      {tab.label}
                      {tab.count > 0 && <span className="ml-1.5 text-[10px] bg-white/20 data-[state=active]:bg-white/20 px-1.5 py-0.5 rounded-full">{tab.count}</span>}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* Capture Details Tab */}
                <TabsContent value="capture" className="mt-4">
                  <Card className="shadow-sm">
                    <CardContent className="p-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                        {/* System fields */}
                        {[
                          { label: "Name", value: lead.name },
                          { label: "Email", value: lead.email },
                          { label: "Phone", value: lead.phone },
                          { label: "Company", value: lead.company },
                          { label: "City", value: lead.city },
                          { label: "Source", value: lead.source },
                          { label: "Stage", value: lead.stage },
                          { label: "Deal Value", value: lead.deal_value ? `₹${Number(lead.deal_value).toLocaleString("en-IN")}` : null },
                          { label: "Assigned To", value: lead.assigned_to_name },
                          { label: "Lead Status", value: lead.lead_status },
                          { label: "Created", value: formatDateDisplay(lead.created_at) },
                          { label: "Remark", value: lead.remark },
                        ].filter(f => f.value && f.value !== "N/A").map(f => (
                          <div key={f.label} className="py-2 border-b border-slate-50">
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{f.label}</p>
                            <p className="text-sm font-medium text-slate-900 mt-0.5">{f.value}</p>
                          </div>
                        ))}
                        {/* Custom fields from form builder */}
                        {customFields.filter(f => {
                          const val = customValues[f.field_key] || customValues[f.field_id];
                          return val && val !== "N/A" && val !== "-";
                        }).map(f => (
                          <div key={f.field_key} className="py-2 border-b border-slate-50">
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{f.field_label}</p>
                            <p className="text-sm font-medium text-slate-900 mt-0.5">{customValues[f.field_key] || customValues[f.field_id]}</p>
                          </div>
                        ))}
                      </div>
                      {/* All raw lead data for fields not covered above — grouped into
                          two sections: Form Answers (fb_q_*) and Other metadata. */}
                      {(() => {
                        const shown = new Set(["name","email","phone","company","city","source","stage","deal_value","assigned_to_name","lead_status","created_at","remark","_id","lead_id","business_id","assigned_to","created_by","updated_at","avatar_url","meeting_completed","follow_up_type","next_meeting","meeting_notes","interaction_count","deals_count","last_activity_date","follow_up_date","follow_up_reminder","stage_change_note","lead_opened_at","first_response_at","created_by_name"]);
                        const isEmpty = (v) => v == null || v === "" || v === "null" || v === "undefined" || (typeof v === "object" && Object.keys(v).length === 0);
                        const formatVal = (v) => {
                          if (v && typeof v === "object") { try { return JSON.stringify(v); } catch (_) { return String(v); } }
                          // Convert snake_case values like "1_month" → "1 month" for readability
                          return String(v).replace(/_/g, " ");
                        };
                        const labelize = (k) => k.replace(/^fb_q_/, "").replace(/^fb_/, "FB ").replace(/_/g, " ");
                        const formAnswers = [], otherFields = [];
                        for (const [k, v] of Object.entries(lead)) {
                          if (shown.has(k) || isEmpty(v)) continue;
                          if (k.startsWith("fb_q_")) formAnswers.push([k, v]);
                          else otherFields.push([k, v]);
                        }
                        return (
                          <>
                            {formAnswers.length > 0 && (
                              <div className="mt-6 pt-4 border-t border-slate-100">
                                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3">Form Answers (from Facebook)</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                                  {formAnswers.map(([k, v]) => (
                                    <div key={k} className="py-2 border-b border-slate-50">
                                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{labelize(k)}</p>
                                      <p className="text-sm font-medium text-slate-900 mt-0.5 break-words">{formatVal(v)}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {otherFields.length > 0 && (
                              <div className="mt-6 pt-4 border-t border-slate-100">
                                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3">Additional Metadata</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                                  {otherFields.map(([k, v]) => (
                                    <div key={k} className="py-2 border-b border-slate-50">
                                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{labelize(k)}</p>
                                      <p className="text-sm font-medium text-slate-900 mt-0.5 break-words">{formatVal(v)}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="mt-4">
                  <Card className="shadow-sm">
                    <CardContent className="p-4">
                      {activities.length === 0 ? (
                        <p className="text-center text-slate-400 py-8">No activity recorded yet</p>
                      ) : (
                        <div className="space-y-4">
                          {activities.map((act, i) => (
                            <div key={i} className="flex gap-3 pb-4 border-b border-slate-50 last:border-0">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                <Clock className="w-4 h-4 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-900">{act.description || act.action_type || "Activity"}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{formatDateDisplay(act.created_at)}{act.created_by_name ? ` by ${act.created_by_name}` : ""}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Remarks Tab */}
                <TabsContent value="remarks" className="mt-4">
                  <Card className="shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex gap-2 mb-4">
                        <Input
                          placeholder="Add a remark..."
                          value={newRemark}
                          onChange={e => setNewRemark(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleAddRemark()}
                          className="flex-1"
                          data-testid="lead-remark-input"
                        />
                        <Button onClick={handleAddRemark} size="sm" className="bg-blue-600 hover:bg-blue-700">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                      {loadingRemarks ? <p className="text-center text-slate-400 py-4">Loading...</p> : (
                        <div className="space-y-3">
                          {remarks.length === 0 && <p className="text-center text-slate-400 py-6">No remarks yet</p>}
                          {(Array.isArray(remarks) ? remarks : []).map((r, i) => (
                            <div key={r._id || i} className="flex gap-3 pb-3 border-b border-slate-50 last:border-0 group">
                              <Avatar className="w-8 h-8 shrink-0">
                                <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">{getInitials(r.created_by_name || "U")}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-xs font-medium text-slate-500">{r.created_by_name || "Unknown"}</p>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-slate-400">{formatDateDisplay(r.created_at)}</span>
                                    <button onClick={() => handleDeleteRemark(r._id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                                <p className="text-sm text-slate-700 mt-0.5">{r.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Follow-ups Tab */}
                <TabsContent value="followups" className="mt-4 space-y-4">
                  {/* Schedule Follow-up Card */}
                  <Card className="shadow-sm border-slate-200/60 overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 flex items-center justify-between">
                      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Schedule Follow-up
                      </h3>
                      <Button size="sm" variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 h-7 text-xs"
                        onClick={() => setShowFollowUpForm(!showFollowUpForm)}>
                        {showFollowUpForm ? <XCircle className="w-4 h-4" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> New</>}
                      </Button>
                    </div>
                    {showFollowUpForm && (
                      <CardContent className="p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Date & Time</label>
                            <Input type="datetime-local" value={followUpData.date} onChange={e => setFollowUpData(d => ({ ...d, date: e.target.value }))}
                              className="h-11 rounded-xl border-slate-200 focus:border-indigo-400 focus:ring-indigo-400/20" />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Type</label>
                            <Select value={followUpData.type} onValueChange={v => setFollowUpData(d => ({ ...d, type: v }))}>
                              <SelectTrigger className="h-11 rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="call"><div className="flex items-center gap-2"><Phone className="w-4 h-4 text-blue-500" /> Call</div></SelectItem>
                                <SelectItem value="meeting"><div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-500" /> Meeting</div></SelectItem>
                                <SelectItem value="whatsapp"><div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-green-500" /> WhatsApp</div></SelectItem>
                                <SelectItem value="email"><div className="flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-500" /> Email</div></SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Notes</label>
                          <Input placeholder="Add notes about this follow-up..." value={followUpData.notes}
                            onChange={e => setFollowUpData(d => ({ ...d, notes: e.target.value }))}
                            className="h-11 rounded-xl border-slate-200 focus:border-indigo-400 focus:ring-indigo-400/20" />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 rounded-lg px-5" onClick={handleScheduleFollowUp}>
                            <CheckCircle className="w-4 h-4 mr-1.5" /> Schedule Follow-up
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-lg" onClick={() => setShowFollowUpForm(false)}>Cancel</Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Active Follow-up */}
                  {lead.next_meeting && !lead.meeting_completed && (
                    <Card className="shadow-sm border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
                      <CardContent className="p-0">
                        <div className="flex items-stretch">
                          <div className="w-1.5 bg-amber-400 shrink-0" />
                          <div className="flex-1 p-4 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                                {lead.follow_up_type === "call" ? <Phone className="w-5 h-5 text-amber-600" /> :
                                 lead.follow_up_type === "meeting" ? <Calendar className="w-5 h-5 text-amber-600" /> :
                                 lead.follow_up_type === "whatsapp" ? <MessageSquare className="w-5 h-5 text-amber-600" /> :
                                 <Clock className="w-5 h-5 text-amber-600" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-bold text-amber-900 capitalize">{lead.follow_up_type || "Follow-up"}</p>
                                  <Badge className="bg-amber-200 text-amber-800 text-[10px] px-2 py-0 border-0 animate-pulse">Upcoming</Badge>
                                </div>
                                <p className="text-sm text-amber-700 mt-0.5 font-medium">{formatDateDisplay(lead.next_meeting)}</p>
                                {lead.meeting_notes && <p className="text-xs text-amber-600 mt-1 bg-amber-100/50 px-2 py-1 rounded-md inline-block">{lead.meeting_notes}</p>}
                              </div>
                            </div>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm px-4" onClick={handleCompleteFollowUp}>
                              <CheckCircle className="w-4 h-4 mr-1.5" /> Mark Done
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Follow-up History */}
                  <Card className="shadow-sm border-slate-200/60">
                    <CardContent className="p-0">
                      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" /> Follow-up History
                        </h3>
                        <span className="text-xs text-slate-400">{(followups.history || []).length} records</span>
                      </div>
                      {loadingFollowups ? (
                        <div className="text-center text-slate-400 text-sm py-10">Loading...</div>
                      ) : (!followups.history || followups.history.length === 0) && !lead.next_meeting ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                            <Calendar className="w-7 h-7 text-slate-300" />
                          </div>
                          <p className="text-slate-400 text-sm font-medium">No follow-ups yet</p>
                          <p className="text-slate-300 text-xs mt-1">Schedule your first follow-up to start tracking</p>
                          <Button size="sm" variant="outline" className="mt-4 rounded-lg" onClick={() => setShowFollowUpForm(true)}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Schedule Follow-up
                          </Button>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-50">
                          {(followups.history || []).map((f, i) => {
                            const isCompleted = f.meeting_completed;
                            const fType = f.follow_up_type || f.type || "follow_up";
                            return (
                              <div key={i} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/50 transition-colors group">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                  isCompleted ? "bg-emerald-100 border border-emerald-200" : "bg-red-100 border border-red-200"
                                }`}>
                                  {fType === "call" ? <Phone className={`w-4 h-4 ${isCompleted ? "text-emerald-600" : "text-red-500"}`} /> :
                                   fType === "meeting" ? <Calendar className={`w-4 h-4 ${isCompleted ? "text-emerald-600" : "text-red-500"}`} /> :
                                   fType === "whatsapp" ? <MessageSquare className={`w-4 h-4 ${isCompleted ? "text-emerald-600" : "text-red-500"}`} /> :
                                   fType === "email" ? <Mail className={`w-4 h-4 ${isCompleted ? "text-emerald-600" : "text-red-500"}`} /> :
                                   isCompleted ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-semibold text-slate-800 capitalize">{fType.replace("_", " ")}</p>
                                    <Badge className={`text-[10px] px-2 py-0 border-0 ${isCompleted ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                                      {isCompleted ? "Completed" : "Missed"}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-0.5">{formatDateDisplay(f.date || f.next_meeting)}</p>
                                  {f.meeting_notes && <p className="text-xs text-slate-500 mt-1 bg-slate-50 px-2 py-1 rounded-md inline-block">{f.meeting_notes}</p>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

              </Tabs>
            </div>
          </div>
        </div>

      {/* Dialogs */}
      <StageChangeDialog open={showStageDialog} onOpenChange={setShowStageDialog}
        pendingStage={pendingStage} stageChangeNote={stageChangeNote} setStageChangeNote={setStageChangeNote}
        saving={saving} onConfirm={confirmStageChange} />
      <EditLeadDialog open={showEditDialog} onOpenChange={setShowEditDialog}
        editForm={editForm} setEditForm={setEditForm} saving={saving} onSave={handleUpdateLead}
        customValues={customValues} onCustomValueChange={setCustomValue} />
      <AddTaskDialog open={showTaskDialog} onOpenChange={setShowTaskDialog}
        newTask={newTask} setNewTask={setNewTask} onSave={handleSaveTask} />
      <AddMeetingDialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}
        newMeeting={newMeeting} setNewMeeting={setNewMeeting}
        onSave={() => { toast.info("Meeting scheduling coming soon"); setShowMeetingDialog(false); }} />
    </div>
  );
}
